import type { RootState } from '@tunasong/redux'
import { useSelector } from 'react-redux'

export interface AnchoredState {
  /** Element is the target of at least one personal note. */
  anchored: boolean
  /** Element matches the currently-expanded note in the sidebar. */
  active: boolean
  /**
   * Element matches the row currently hovered or focused in the sidebar, or is the line of a new note in Perform
   * (`landingAnchoredElementId`, GH #2907): the same tint.
   */
  hovered: boolean
}

/**
 * Reads the personal-note anchor highlight flags for a single block element id.
 * Returns all-false for unidentified elements (Slate void leaves, drafts).
 *
 * The personal-note plugin's `AnchoredNotesController` keeps
 * `anchoredElementIds` and `activeAnchoredElementId` in sync with the user's
 * notes; the sidebar's `entity-notes.tsx` row handlers drive
 * `hoveredAnchoredElementId`. The editor stays plugin-agnostic — this hook
 * is a one-way Redux read so blocks can render the correct halo without
 * importing from the personal-note plugin.
 */
export const useAnchoredState = (elementId: string | undefined): AnchoredState => {
  // Subscribe to each primitive separately. With three independent boolean
  // selectors, an unrelated change (e.g. a sibling block becoming active)
  // re-runs only the selectors whose value actually flipped — useSelector
  // shallow-compares booleans, so React skips the re-render when nothing
  // changed for this particular element.
  const anchored = useSelector(
    (state: RootState) => elementId !== undefined && state.ui.anchoredElementIds.includes(elementId)
  )
  const active = useSelector(
    (state: RootState) => elementId !== undefined && state.ui.activeAnchoredElementId === elementId
  )
  const hovered = useSelector(
    (state: RootState) =>
      elementId !== undefined &&
      (state.ui.hoveredAnchoredElementId === elementId || state.ui.landingAnchoredElementId === elementId)
  )
  return { anchored, active, hovered }
}
